import { useState, useEffect, useRef, createContext, useContext } from "react";
import { toast } from "react-toastify";

import { appSettings } from "../config/appConfig";

const AppContext = createContext();

export const AppContextProvider = ({ children }) => {
  const baseUrl = appSettings.serverBaseUrl + "/partner";

  const [isLoading, setIsLoading] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState({});
  const [daypart, setDaypart] = useState("");

  const [registrationsByPartner, setRegistrationsByPartner] = useState([]);
  const [wabmActiveUsersByPartnerData, setWabmActiveUsersByPartnerData] =
    useState([]);
  const [wabmUserRechargeHistory, setWabmUserRechargeHistory] = useState([]);
  const [wabmUserAgentsList, setWabmUserAgentsList] = useState([]);
  const [allRechargesData, setAllRechargesData] = useState([]);
  const [selectedUserWabCreds, setSelectedUserWabCreds] = useState({});
  const [campaignsData, setCampaignsData] = useState({});
  const [platformFeeData, setPlatformFeeData] = useState([]);
  const [communicationChannelPricingData, setCommunicationChannelPricingData] =
    useState([]);
  const [partnerPricingData, setPartnerPricingData] = useState({
    channelPricing: [],
    userPricing: [],
  });
  const [isMigratePMLoading, setIsMigratePMLoading] = useState(false);
  const activeUsersRequestRef = useRef(null);

  // ── Auto login-check on mount ──────────────────────────────────────────────
  useEffect(() => {
    loginCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const daypartFunction = () => {
    let currentHour = new Date().getHours();
    if (currentHour >= 5 && currentHour < 12) {
      setDaypart("Morning");
    } else if (currentHour >= 12 && currentHour < 16) {
      setDaypart("Afternoon");
    } else {
      setDaypart("Evening");
    }
  };

  // ── Auth: login check (GET) ────────────────────────────────────────────────
  const loginCheck = () => {
    setIsLoading(true);
    fetch(`${appSettings.serverBaseUrl}/api/login`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.isValidToken) {
          setIsLoggedIn(true);
          setPartnerInfo((prev) => ({ ...prev, ...response }));
        } else {
          setIsLoggedIn(false);
        }
        setIsLoading(false);
        setIsAuthChecking(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setIsLoggedIn(false);
        setIsAuthChecking(false);
      });
  };

  // ── Auth: login (POST) — Corp ID + Agent ID + Password ────────────────────
  const apiCallLogin = (corporateId, agentName, password) => {
    setIsLoading(true);
    fetch(`${appSettings.serverBaseUrl}/api/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        corporateId,
        agentName,
        password,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.isValidToken === true) {
          setIsLoggedIn(true);
          if (response?.message) toast.success(response.message);
          setPartnerInfo((prev) => ({ ...prev, ...response }));
        } else {
          setIsLoggedIn(false);
          if (response?.Error) toast.error(response.Error);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error("Failed to make login request");
        setIsLoading(false);
        setIsLoggedIn(false);
      });
  };

  // ── Auth: logout (GET) ────────────────────────────────────────────────────
  const apiCallLogout = () => {
    setIsLoading(true);
    fetch(`${appSettings.serverBaseUrl}/api/logout`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.isValidToken === false) {
          setIsLoggedIn(false);
          setPartnerInfo({});
          toast.success("Logged out successfully");
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoggedIn(false);
        setIsLoading(false);
      });
  };

  const getActiveUsersByPartner = () => {
    if (activeUsersRequestRef.current) {
      return activeUsersRequestRef.current;
    }

    const request = fetch(
      `${baseUrl}/get-active-users-by-partner?page_size=1000`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          const users = response?.usersData ?? response?.activeUsers;
          if (Array.isArray(users)) {
            setWabmActiveUsersByPartnerData((previousUsers) =>
              users.length === 0 && previousUsers.length > 0
                ? previousUsers
                : users,
            );
          }
        }
      })
      .catch((error) => {
        toast.error("Couldn't get all users data.");
      })
      .finally(() => {
        activeUsersRequestRef.current = null;
      });

    activeUsersRequestRef.current = request;
    return request;
  };

  const getRegistrationsByPartner = () => {
    fetch(`${baseUrl}/get-registrations-by-partner?page_size=1000`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else if (Array.isArray(response?.registrations)) {
          setRegistrationsByPartner(
            response.registrations.map((registration) => ({
              id: registration.reg_id_n,
              regDate: registration.reg_date_d,
              firstName: registration.reg_firstname_c,
              lastName: registration.reg_lastname_c,
              businessType: registration.reg_business_type_c,
              email: registration.reg_email_c,
              phoneNumber: registration.phone_no_c,
              businessName: registration.business_name_c,
              isAccountRegistered: registration.isAccountRegistered,
              registrationStatus:
                Number(registration.isAccountRegistered) === 1
                  ? "Confirmed"
                  : "Pending",
              location: registration.location,
              designation: registration.designation,
              taxId: registration.tax_id,
              referralCode: registration.referral_code,
              referralPartnerId: registration.referral_partner_id,
            })),
          );
        }
      })
      .catch((error) => {
        toast.error("Couldn't get registered users data.");
      });
  };

  const getUserWabCreds = (agentId) => {
    fetch(`${baseUrl}/get-user-wab-creds`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ agentId }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          setSelectedUserWabCreds(response);
        }
      })
      .catch((error) => {
        toast.error(error?.Error);
      });
  };

  const updateUserWabInfo = (corpId, infoType, infoValue) => {
    fetch(`${baseUrl}/update-wabm-user-wab-info`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ corpId, infoType, infoValue }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          toast.success(response?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.Error);
      });
  };

  const fetchUserRechargeHistory = (userId) => {
    fetch(`${baseUrl}/fetch-recharge-history?userId=${userId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else if (response?.result) {
          setWabmUserRechargeHistory(response?.result);
        }
      })
      .catch((error) => {
        toast.error("Couldn't get client's recharge history.");
      });
  };

  const getUserAgentsList = (userId) => {
    fetch(`${baseUrl}/get-user-agents?userId=${userId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else if (response?.data) {
          setWabmUserAgentsList(response?.data);
        }
      })
      .catch((error) => {
        toast.error(error?.Error);
      });
  };

  const getAllRechargesData = () => {
    return fetch(`${baseUrl}/all-recharges-data`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else if (response?.data) {
          setAllRechargesData(response?.data);
        }
      })
      .catch((error) => {
        toast.error("Couldn't get all recharges data.");
      });
  };

  const getCampaignsData = (pageSize, userId, lastId) => {
    fetch(`${baseUrl}/get-wa-campaigns-data`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ pageSize, userId, lastId }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          setCampaignsData(response);
        }
      })
      .catch((error) => {
        console.error("getCampaignsData error", error);
        toast.error("Couldn't get broadcast/secheduled campaigns data.");
      });
  };
  const getPlatformFeeData = (userId) => {
    fetch(`${baseUrl}/get-platform-fee?userId=${userId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          setPlatformFeeData(response);
        }
      })
      .catch((error) => {
        console.error("getPlatformFeeData error", error);
        toast.error("Couldn't get platform fee data.");
      });
  };

  const getCommunicationChannelPricingData = () => {
    fetch(`${baseUrl}/get-communication-channel-pricing`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          setCommunicationChannelPricingData(response);
        }
      })
      .catch((error) => {
        console.error("getCommunicationChannelPricingData error", error);
        toast.error("Couldn't get communication channel pricing data.");
      });
  };

  const getPartnerPricingData = () => {
    return fetch(`${baseUrl}/pricing-data`, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (res) => {
        const response = await res.json();
        if (!res.ok || response?.Error) {
          throw new Error(response?.Error || "Unable to get pricing data");
        }
        const data = {
          channelPricing: response?.channelPricing ?? [],
          userPricing: response?.userPricing ?? [],
        };
        setPartnerPricingData(data);
        return data;
      })
      .catch((error) => {
        toast.error(error?.message || "Couldn't get pricing data.");
        throw error;
      });
  };

  const savePlatformFee = ({
    platformFeeId,
    channelName,
    userChannelId,
    countryCode,
    countryCodeIso3,
    entityType,
    coinsCost,
    priceWef,
    userId,
    remarks,
    isIncludeChannelPricing,
  }) => {
    fetch(`${baseUrl}/save-platform-fee`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        platformFeeId,
        channelName,
        userChannelId,
        countryCode,
        countryCodeIso3,
        entityType,
        coinsCost,
        priceWef,
        userId,
        remarks,
        isIncludeChannelPricing,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          toast("Saved platform fee");
        }
      })
      .catch((error) => {
        console.error("savePlatformFee error", error);
        toast.error("Couldn't save platform fee data.");
      });
  };

  const migratePricingModel = (payload) => {
    setIsMigratePMLoading(true);
    fetch(`${baseUrl}/migrate-pricing-model`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        payload,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response?.Error) {
          toast.error(response?.Error);
        } else {
          toast("Migrate pricing model");
        }
      })
      .catch((error) => {
        console.error("migrate Pricing model error", error);
        toast.error("Couldn't migrate Pricing mode.");
      })
      .finally(() => {
        setIsMigratePMLoading(false);
      });
  };

  return (
    <AppContext.Provider
      value={{
        // ── Auth ──────────────────────────────────────────────────────────
        loginCheck,
        apiCallLogin,
        apiCallLogout,
        // ── UI helpers ───────────────────────────────────────────────────
        daypartFunction,
        // ── Partner data ─────────────────────────────────────────────────
        getActiveUsersByPartner,
        getRegistrationsByPartner,
        getUserWabCreds,
        updateUserWabInfo,
        fetchUserRechargeHistory,
        getUserAgentsList,
        getAllRechargesData,
        getCampaignsData,
        getPlatformFeeData,
        getCommunicationChannelPricingData,
        getPartnerPricingData,
        savePlatformFee,
        migratePricingModel,
        // ── State ────────────────────────────────────────────────────────
        isLoading,
        isAuthChecking,
        isLoggedIn,
        partnerInfo,
        daypart,
        wabmActiveUsersByPartnerData,
        registrationsByPartner,
        wabmUserRechargeHistory,
        wabmUserAgentsList,
        allRechargesData,
        selectedUserWabCreds,
        campaignsData,
        platformFeeData,
        communicationChannelPricingData,
        partnerPricingData,
        isMigratePMLoading,
        // ── Setters ──────────────────────────────────────────────────────
        setIsLoggedIn,
        setIsLoading,
        setDaypart,
        setSelectedUserWabCreds,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
